/** Compound equipment tags — one drawing label that names several plant assets.
 *
 *  Draughtsmen write two pumps sharing a symbol as "109-DA/DB", and two separate
 *  assets sharing a note as "A-101-J,A-102-J". Each is one tag on the drawing and
 *  several assets in the plant register, so publishing flags it until a reviewer
 *  splits it. These helpers detect the shape and propose the split.
 */

/** The separators a draughtsman uses to fold several assets into one label. */
const COMPOUND_SEPARATORS = /[,/]/;

/** A parenthesised segment — a sheet count "(1/2)" or an alias "(AT-1004/AT-1004A)". */
const PARENTHESIZED_SEGMENT = /\([^()]*\)/g;

/** The character set a tag part is written from. */
const TAG_LIKE_PART = /^[A-Za-z0-9][A-Za-z0-9 _-]*$/;

/** A whole tag written out, long enough to carry a plant number — "3/4" is a pipe size. */
const MINIMUM_LEADING_PART_LENGTH = 2;

/** A tag part is one word, or two when a draughtsman spaced it ("A-107 JA"). */
const MAXIMUM_PART_WORDS = 2;

/** The trailing letters of a tag — "104-JA" → "JA", "P-601A" → "A", "U-GA1202" → "". */
const TRAILING_ALPHABETIC_RUN = /[A-Za-z]+$/;

/** True when one part of a separated tag could itself name a plant asset.
 *
 *  Keeps prose out ("SEAL VENT", "PA FACILITY") while admitting the spaced tags a
 *  draughtsman writes ("A-107 JA", "U-GA103 A").
 */
function isTagLikePart(part: string): boolean {
  if (!TAG_LIKE_PART.test(part)) return false;
  const words = part.split(/\s+/);
  if (words.length > MAXIMUM_PART_WORDS) return false;
  if (words.length === MAXIMUM_PART_WORDS && !/\d/.test(part)) return false;
  return true;
}

/** The tag-like parts a label separates into, ignoring anything parenthesised. */
function separatedParts(tag: string): string[] {
  return tag
    .replace(PARENTHESIZED_SEGMENT, ' ')
    .split(COMPOUND_SEPARATORS)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** True when a separator outside parentheses joins parts that each name an asset.
 *
 *  Both separators also carry meanings that name a single asset: a parenthesised
 *  sheet count ("U-EA301(1/2)"), a pipe size ("3/4"), a descriptive label ("LUBE
 *  OIL CONSOLE FOR 104-JA/JAM"), and the prose an extraction model writes into an
 *  untagged instrument's label. The parts, not the separator, decide. This is the
 *  same rule the publication service applies, so the split is offered exactly
 *  where publishing raises the warning.
 */
export function isCompoundTag(tag: string | null | undefined): boolean {
  const parts = separatedParts((tag ?? '').trim());
  if (parts.length < 2) return false;
  const leadingPart = parts[0];
  if (leadingPart.length < MINIMUM_LEADING_PART_LENGTH || !/\d/.test(leadingPart)) return false;
  return parts.every(isTagLikePart);
}

/** The tags a compound tag proposes, in drawing order.
 *
 *  The first part is a whole tag. Each later part either repeats that — written
 *  out in full — or abbreviates the tag before it down to the piece that changes.
 *  What the part carries says which it is:
 *
 *    1. Its own number and a hyphen — a whole tag, kept as written.
 *    2. No number at all — a suffix, replacing the previous tag's trailing letters,
 *       or appended when the previous tag ends in a digit.
 *    3. A number but no hyphen — it replaces the token after the previous tag's
 *       last hyphen.
 *
 *    "109-DA/DB"             → ["109-DA", "109-DB"]
 *    "104-JA/JAM"            → ["104-JA", "104-JAM"]
 *    "108-J/JA"              → ["108-J", "108-JA"]
 *    "A-101-J,A-102-J"       → ["A-101-J", "A-102-J"]
 *    "A-101-J/JT,A-102-J/JM" → ["A-101-J", "A-101-JT", "A-102-J", "A-102-JM"]
 *    "P-601A/B"              → ["P-601A", "P-601B"]
 *    "U-GB101 / U-GT101"     → ["U-GB101", "U-GT101"]
 *
 *  A non-compound tag expands to itself. The result is a proposal for a reviewer
 *  to correct, never a decision — the drawing is the only authority on what the
 *  abbreviation meant.
 */
export function expandCompoundTag(tag: string | null | undefined): string[] {
  const expanded: string[] = [];
  let previousTag = '';
  for (const fragment of (tag ?? '').split(COMPOUND_SEPARATORS)) {
    const trimmed = fragment.trim();
    if (!trimmed) continue;
    previousTag = previousTag ? resolveFragment(trimmed, previousTag) : trimmed;
    expanded.push(previousTag);
  }
  return expanded;
}

function resolveFragment(fragment: string, previousTag: string): string {
  const carriesNumber = /\d/.test(fragment);

  if (carriesNumber && fragment.includes('-')) return fragment;

  if (!carriesNumber) {
    const trailingLetters = previousTag.match(TRAILING_ALPHABETIC_RUN);
    return trailingLetters
      ? `${previousTag.slice(0, previousTag.length - trailingLetters[0].length)}${fragment}`
      : `${previousTag}${fragment}`;
  }

  const lastHyphen = previousTag.lastIndexOf('-');
  return lastHyphen === -1 ? fragment : `${previousTag.slice(0, lastHyphen + 1)}${fragment}`;
}
